/* gw-search — custom element for the header search dropdown.

   Replaces Dawn's <predictive-search> + Liquid-rendered results panel.
   Fetches JSON from /search/suggest.json, debounces input, renders a
   two-column dropdown (Suggestions + Products) plus a "Search for X"
   footer link. Works on desktop (inline below the input) and mobile
   (inside the full-viewport modal in gw-header-mobile.liquid).

   XSS safety: this component echoes user-typed strings + server-supplied
   product data back to the DOM. ALL such text is rendered via
   `textContent` or `setAttribute`. There is NO `innerHTML` use anywhere
   in this file, including for Shopify's `styled_text` field.
*/

class GwSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.dropdown = this.querySelector('[data-gw-search-dropdown]');
    this.suggestionsList = this.querySelector('[data-gw-search-suggestions]');
    this.suggestionsHeading = this.querySelector('[data-gw-search-suggestions-heading]');
    this.productsList = this.querySelector('[data-gw-search-products]');
    this.productsHeading = this.querySelector('[data-gw-search-products-heading]');
    this.footer = this.querySelector('[data-gw-search-footer]');
    this.footerQuery = this.querySelector('[data-gw-search-footer-query]');
    this.emptyState = this.querySelector('[data-gw-search-empty]');

    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.searchUrl = (window.routes && window.routes.search_url) || '/search';
    this.suggestUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  }

  connectedCallback() {
    if (!this.input || !this.dropdown) return;
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keyup', this.onDocumentKeyup.bind(this));
    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();
    if (!query) {
      this.close();
      return;
    }
    this.debounceTimer = setTimeout(() => this.search(query), 250);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query) this.search(query);
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.input.blur();
    }
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) this.close();
  }

  onDocumentKeyup(e) {
    if (e.key === 'Escape') this.close();
  }

  onSubmit(e) {
    if (!this.input.value.trim()) e.preventDefault();
  }

  async search(query) {
    if (this.cache.has(query)) {
      this.render(this.cache.get(query), query);
      return;
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product,query');
    params.set('resources[limit]', '6');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    try {
      const url = `${this.suggestUrl}.json?${params.toString()}`;
      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(`gw-search: HTTP ${res.status}`);
      const data = await res.json();
      this.cache.set(query, data);
      this.render(data, query);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('gw-search: fetch failed', err);
        this.renderEmpty(query);
      }
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('gw-search--loading', isLoading);
  }

  // Replace all children of `parent` with `nodes`. Safer than parent.innerHTML = ''.
  replaceChildrenSafe(parent, nodes) {
    while (parent.firstChild) parent.removeChild(parent.firstChild);
    if (Array.isArray(nodes)) {
      for (const n of nodes) parent.appendChild(n);
    } else if (nodes) {
      parent.appendChild(nodes);
    }
  }

  // Parse Shopify's `styled_text` (which contains `<mark>`/`<b>`/`<strong>`
  // tags around matched portions and `<span>` wrappers around the rest) into
  // a safe DocumentFragment. Highlight tags become `<mark>` nodes; other
  // tags (e.g. <span>) are unwrapped — their text content is preserved but
  // the wrapper is dropped. NEVER uses innerHTML, so any unexpected markup
  // (script, iframe, etc.) is rendered as inert text by createTextNode.
  buildStyledTextFragment(raw) {
    const frag = document.createDocumentFragment();
    if (!raw) return frag;
    const TAG = /(<\/?[a-zA-Z][^>]*>)/;
    const tokens = String(raw).split(TAG);
    const stack = [frag];
    for (const token of tokens) {
      if (!token) continue;
      if (token.charAt(0) === '<') {
        const isClosing = token.charAt(1) === '/';
        const nameMatch = token.slice(isClosing ? 2 : 1).match(/^[a-zA-Z]+/);
        const name = nameMatch ? nameMatch[0].toLowerCase() : '';
        const isHighlight = name === 'mark' || name === 'b' || name === 'strong' || name === 'em';
        if (isHighlight) {
          if (isClosing) {
            if (stack.length > 1) stack.pop();
          } else {
            const el = document.createElement('mark');
            stack[stack.length - 1].appendChild(el);
            stack.push(el);
          }
        }
        // Non-highlight tags (span, div, etc.) are silently dropped — their
        // text content is still appended via subsequent text-token iterations.
      } else {
        stack[stack.length - 1].appendChild(document.createTextNode(token));
      }
    }
    return frag;
  }

  buildSuggestionItem(q) {
    const li = document.createElement('li');
    li.className = 'gw-search__suggestion';
    const a = document.createElement('a');
    a.className = 'gw-search__suggestion-link';
    a.setAttribute('href', q.url || `${this.searchUrl}?q=${encodeURIComponent(q.text || '')}`);
    if (q.styled_text) {
      a.appendChild(this.buildStyledTextFragment(q.styled_text));
    } else {
      a.textContent = q.text || '';
    }
    li.appendChild(a);
    return li;
  }

  buildProductItem(p) {
    const li = document.createElement('li');
    li.className = 'gw-search__product';

    const a = document.createElement('a');
    a.className = 'gw-search__product-link';
    a.setAttribute('href', p.url || '#');

    const imgWrap = document.createElement('div');
    imgWrap.className = 'gw-search__product-image-wrap';
    if (p.image) {
      const img = document.createElement('img');
      img.className = 'gw-search__product-image';
      img.setAttribute('src', p.image);
      img.setAttribute('alt', p.title || '');
      img.setAttribute('width', '48');
      img.setAttribute('height', '48');
      img.setAttribute('loading', 'lazy');
      imgWrap.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'gw-search__product-image gw-search__product-image--empty';
      placeholder.setAttribute('aria-hidden', 'true');
      imgWrap.appendChild(placeholder);
    }
    a.appendChild(imgWrap);

    const info = document.createElement('div');
    info.className = 'gw-search__product-info';

    if (p.vendor) {
      const vendor = document.createElement('div');
      vendor.className = 'gw-search__product-vendor';
      vendor.textContent = p.vendor;
      info.appendChild(vendor);
    }

    const title = document.createElement('div');
    title.className = 'gw-search__product-title';
    title.textContent = p.title || '';
    info.appendChild(title);

    if (p.price) {
      const price = document.createElement('div');
      price.className = 'gw-search__product-price';
      price.textContent = p.price;
      info.appendChild(price);
    }

    a.appendChild(info);
    li.appendChild(a);
    return li;
  }

  render(data, query) {
    const results = (data && data.resources && data.resources.results) || {};
    const queries = results.queries || [];
    const products = results.products || [];

    if (queries.length === 0 && products.length === 0) {
      this.renderEmpty(query);
      return;
    }

    if (this.suggestionsHeading) this.suggestionsHeading.hidden = queries.length === 0;
    this.replaceChildrenSafe(
      this.suggestionsList,
      queries.map((q) => this.buildSuggestionItem(q)),
    );

    if (this.productsHeading) this.productsHeading.hidden = products.length === 0;
    this.replaceChildrenSafe(
      this.productsList,
      products.map((p) => this.buildProductItem(p)),
    );

    if (this.emptyState) this.emptyState.hidden = true;
    this.updateFooter(query);
    this.open();
  }

  renderEmpty(query) {
    this.replaceChildrenSafe(this.suggestionsList, []);
    this.replaceChildrenSafe(this.productsList, []);
    if (this.suggestionsHeading) this.suggestionsHeading.hidden = true;
    if (this.productsHeading) this.productsHeading.hidden = true;
    if (this.emptyState) this.emptyState.hidden = false;
    this.updateFooter(query);
    this.open();
  }

  updateFooter(query) {
    if (this.footerQuery) this.footerQuery.textContent = query;
    if (this.footer) {
      this.footer.setAttribute(
        'href',
        `${this.searchUrl}?q=${encodeURIComponent(query)}`,
      );
    }
  }

  open() {
    this.dropdown.hidden = false;
    this.classList.add('gw-search--open');
  }

  close() {
    this.dropdown.hidden = true;
    this.classList.remove('gw-search--open');
  }
}

if (!customElements.get('gw-search')) {
  customElements.define('gw-search', GwSearch);
}
